import { AxiosResponse } from 'axios'
import { http } from '../realisations/Http.realisation'
import {
    ContractsAttachDocumentResponseT,
    ContractsGetToBeAgreedParamsT,
    ContractsDownloadContractResponseT,
    ContractsDownloadDocumentResponseT,
    ContractsExportExcelParamsT,
    ContractsGetContractResponseT,
    ContractsGetListResponseT,
    ContractsGetParamsT,
    ContractsGetStatusesResponseT,
    ContractsGetToBeAgreedListResponseT,
    ContractsGetTypesResponseT,
    ContractsSendTranscriptParamsT,
    ContractsShowContractResponseT,
    ContractsShowDocumentResponseT,
    ContractsGetTypesParamsT,
    ContractsGetContractByBinResponseT,
    ContractsRegisterGetResponse,
    ContractsRegisterGetParams,
    ContractsRegisterExportExcelParams,
    GetContractRegisterResponse,
    CreateContractRegisterPayload,
    EditContractRegisterPayload,
    GetTemplatesParams,
    GetTemplatesResponse,
    AddTemplatePayload,
    DownloadLogistBidResponse,
    ContractsGetServicesResponse,
    GetConfigListParams,
    GetConfigListResponse,
    GetConfigValueByKeyResponse
} from '../types/contracts.types'

export class ContractsRepository {
    getList(params: ContractsGetParamsT): Promise<AxiosResponse<ContractsGetListResponseT>> {
        return http.get('/contract/api/contract', { params })
    }

    getServices(): Promise<AxiosResponse<ContractsGetServicesResponse>> {
        return http.get('/contract/api/contract-services')
    }

    getTemplates(params: GetTemplatesParams): Promise<AxiosResponse<GetTemplatesResponse>> {
        return http.get('/contract/api/template', { params })
    }

    getArchiveList(params: ContractsGetParamsT): Promise<AxiosResponse<ContractsGetListResponseT>> {
        return http.get('/contract/api/contract/archive', { params })
    }

    getRegisterList(
        params: ContractsRegisterGetParams
    ): Promise<AxiosResponse<ContractsRegisterGetResponse>> {
        return http.get('/contract/api/register-contracts', { params })
    }

    getToBeAgreedList(
        params: ContractsGetToBeAgreedParamsT
    ): Promise<AxiosResponse<ContractsGetToBeAgreedListResponseT>> {
        return http.get('/contract/api/dogovor24', { params })
    }

    getContract(contract_id: number): Promise<AxiosResponse<ContractsGetContractResponseT>> {
        return http.get(`/contract/api/contract/${contract_id}`)
    }

    getContractRegister(id: number): Promise<AxiosResponse<GetContractRegisterResponse>> {
        return http.get(`/contract/api/register-contracts/${id}`)
    }

    getContractByBin(bin: string): Promise<AxiosResponse<ContractsGetContractByBinResponseT>> {
        const params = {
            type_id: 8,
        }

        return http.get(`/contract/api/contract/${bin}/bin`, { params })
    }

    sendTranscript(payload: ContractsSendTranscriptParamsT): Promise<AxiosResponse<void>> {
        return http.post('/contract/api/dogovor24/send-transcript', payload)
    }

    createContract(form_data: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData('/contract/api/contract', form_data)
    }

    createContractRegister(payload: CreateContractRegisterPayload): Promise<AxiosResponse<void>> {
        return http.post('/contract/api/register-contracts', payload)
    }

    addCommentRegister(id: number, comment: string): Promise<AxiosResponse<void>> {
        return http.post(`/contract/api/register-contracts/${id}/comment`, { comment })
    }

    changeStatusRegister(
        id: number,
        statusId: number,
        comment?: string
    ): Promise<AxiosResponse<void>> {
        return http.put(`/contract/api/register-contracts/${id}/status`, { statusId, comment })
    }

    editContractRegister(
        id: number,
        payload: EditContractRegisterPayload
    ): Promise<AxiosResponse<void>> {
        return http.put(`/contract/api/register-contracts/${id}`, payload)
    }

    sendD24(form_data: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData('/contract/api/dogovor24/send-document', form_data)
    }

    addTemplate(form_data: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData('/contract/api/template', form_data)
    }

    attachDocument(form_data: FormData): Promise<AxiosResponse<ContractsAttachDocumentResponseT>> {
        return http.postFormData('/contract/api/contract/additional-document', form_data)
    }

    downloadContract(
        contract_id: number
    ): Promise<AxiosResponse<ContractsDownloadContractResponseT>> {
        return http.get(`/contract/api/contract/${contract_id}/download`, { responseType: 'blob' })
    }

    showContract(contract_id: number): Promise<AxiosResponse<ContractsShowContractResponseT>> {
        return http.get(`/contract/api/contract/${contract_id}/view`, { responseType: 'blob' })
    }

    downloadDocument(
        document_id: number
    ): Promise<AxiosResponse<ContractsDownloadDocumentResponseT>> {
        return http.get(`/contract/api/contract/additional-document/${document_id}/download`, {
            responseType: 'blob',
        })
    }

    showDocument(document_id: number): Promise<AxiosResponse<ContractsShowDocumentResponseT>> {
        return http.get(`/contract/api/contract/additional-document/${document_id}/view`, {
            responseType: 'blob',
        })
    }

    getTypes(params: ContractsGetTypesParamsT): Promise<AxiosResponse<ContractsGetTypesResponseT>> {
        return http.get('/contract/api/type', { params })
    }

    getStatuses(page: number): Promise<AxiosResponse<ContractsGetStatusesResponseT>> {
        const params = {
            page,
        }

        return http.get('/contract/api/status', { params })
    }

    getStatusesRegister(isVisible?: number): Promise<AxiosResponse<ContractsGetStatusesResponseT>> {
        return http.get('/contract/api/register-contract/statuses', {
            params: { isVisible },
        })
    }

    exportExcel(params: ContractsExportExcelParamsT): Promise<any> {
        return http.get(`/contract/api/contract/export`, { params, responseType: 'blob' })
    }

    exportExcelRegister(params: ContractsRegisterExportExcelParams): Promise<any> {
        return http.get(`/contract/api/register-contracts/report`, { params, responseType: 'blob' })
    }

    downloadTranscript(transcript_id: number): Promise<AxiosResponse<any>> {
        return http.get(`/contract/api/dogovor24/download/${transcript_id}`, {
            responseType: 'blob',
        })
    }

    updateContract(id: number, form_data: FormData): Promise<AxiosResponse<void>> {
        return http.post(`/contract/api/contract/${id}`, form_data)
    }

    deleteContract(contract_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/contract/api/dogovor24/destroy/${contract_id}`)
    }

    deleteContractById(contract_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/contract/api/contract/${contract_id}`)
    }

    deleteAdditionalContract(contract_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/contract/api/contract/additional-document/${contract_id}`)
    }

    checkIsSigned(id: number): Promise<AxiosResponse<void>> {
        return http.put(`/contract/api/dogovor24/check-is-signed/${id}`, { id })
    }

    deleteTemplate(id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/contract/api/template/${id}`)
    }

    editTemplate(id: number, form_data: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData(`/contract/api/template/${id}`, form_data)
    }

    downloadLogistBid(id: number): Promise<AxiosResponse<DownloadLogistBidResponse>> {
        return http.get(`/contract/api/contract/additional-document/${id}/download-bid`, {
            responseType: 'blob',
        })
    }

    getConfigList(params: GetConfigListParams): Promise<AxiosResponse<GetConfigListResponse>> {
        return http.get('/contract/api/config', { params })
    }

    getConfigValueByKey(key: string): Promise<AxiosResponse<GetConfigValueByKeyResponse>> {
        return http.get(`/contract/api/config/${key}`)
    }

    updateConfigKey(key: string, value: string[]): Promise<AxiosResponse<void>> {
        return http.put(`/contract/api/config/${key}`, { value })
    }
}
